import React, { useEffect, useState } from 'react';
import {
  Dialog, DialogContent, Box, Stack, Typography, Button, IconButton, Tooltip,
} from '@mui/material';
import UndoRoundedIcon from '@mui/icons-material/UndoRounded';
import DeleteOutlineRoundedIcon from '@mui/icons-material/DeleteOutlineRounded';
import { dataFetcher, endpoints } from 'Api';

const nameOf = (c) => c?.display_name || `${c?.firstname || ''} ${c?.lastname || ''}`.trim() || c?.email?.split('@')[0] || '—';

// Matches the /access three-dot popover: frosted dark panel, white text.
const C = {
  paper: 'rgba(18,20,28,0.92)',
  border: 'rgba(255,255,255,0.08)',
  text: '#f5f5f7',
  muted: 'rgba(245,245,247,0.60)',
  hover: 'rgba(255,255,255,0.08)',
  chip: 'rgba(255,255,255,0.10)',
  danger: '#ff6b6b',
};

/**
 * Manage-projector popover: lists every projector you have access to, each with
 * a remove icon that stages it for removal (drops it from the list). An Undo
 * next to Close restores the last removed; Cancel discards all staged changes;
 * Save commits them (deleteFromViewer per id) and refreshes /access.
 */
const ManageProjectorModal = ({ open, connections = [], onClose, onSaved, setSnackbar }) => {
  const [removedIds, setRemovedIds] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => { if (open) setRemovedIds([]); }, [open]);

  const list = connections.filter((c) => c?.firstname !== 'Tutorials');
  const visible = list.filter((c) => !removedIds.includes(c.id));

  const remove = (id) => setRemovedIds((prev) => [...prev, id]);
  const undo = () => setRemovedIds((prev) => prev.slice(0, -1));
  const close = () => { if (!saving) onClose(); };

  const save = async () => {
    if (!removedIds.length) { onClose(); return; }
    setSaving(true);
    try {
      await Promise.all(removedIds.map((id) => dataFetcher(endpoints.deleteFromViewer, { id })));
      if (onSaved) onSaved();
      if (setSnackbar) setSnackbar({ isVisible: true, type: 'success', message: `Removed ${removedIds.length} projector${removedIds.length > 1 ? 's' : ''}` });
      onClose();
    } catch (e) {
      if (setSnackbar) setSnackbar({ isVisible: true, type: 'error', message: 'Could not remove — please try again' });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={close}
      maxWidth="xs"
      fullWidth
      PaperProps={{
        sx: {
          // Match the /access three-dot popover exactly.
          borderRadius: 3,
          bgcolor: C.paper,
          color: C.text,
          backgroundImage: 'none',
          border: `1px solid ${C.border}`,
          backdropFilter: 'blur(22px)',
          WebkitBackdropFilter: 'blur(22px)',
          boxShadow: '0 20px 50px rgba(0,0,0,0.5)',
          // Rendered outside V2ThemeProvider (raw on /access), so force the app
          // font instead of MUI's default Roboto.
          fontFamily: "'Montserrat', sans-serif",
          '& .MuiTypography-root, & .MuiButtonBase-root, & .MuiButton-root': { fontFamily: "'Montserrat', sans-serif" },
        },
      }}
      slotProps={{ backdrop: { sx: { backgroundColor: 'rgba(0,0,0,0.55)', backdropFilter: 'blur(6px)', WebkitBackdropFilter: 'blur(6px)' } } }}
    >
      <DialogContent sx={{ p: 0 }}>
        <Stack direction="row" sx={{ alignItems: 'center', justifyContent: 'space-between', p: 2, borderBottom: `1px solid ${C.border}` }}>
          <Box sx={{ minWidth: 0 }}>
            <Typography sx={{ fontWeight: 800, fontSize: 17, color: C.text }}>Manage projector</Typography>
            <Typography variant="caption" sx={{ color: C.muted }}>Remove projectors you no longer want access to</Typography>
          </Box>
          <Stack direction="row" spacing={0.5} sx={{ alignItems: 'center', flexShrink: 0 }}>
            <Tooltip title={removedIds.length ? 'Undo last removal' : 'Nothing to undo'}>
              <span>
                <IconButton size="small" onClick={undo} disabled={!removedIds.length || saving} sx={{ color: C.muted, '&:hover': { color: C.text } }}>
                  <UndoRoundedIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
            <Button
              size="small"
              onClick={close}
              disabled={saving}
              sx={{ textTransform: 'none', fontWeight: 600, minWidth: 0, px: 1, color: C.muted, '&:hover': { color: C.text, bgcolor: C.hover } }}
            >
              Close
            </Button>
          </Stack>
        </Stack>

        <Box sx={{ maxHeight: '52vh', overflowY: 'auto', p: 1 }}>
          {visible.length === 0 ? (
            <Typography variant="body2" sx={{ color: C.muted, textAlign: 'center', py: 5 }}>
              {list.length === 0 ? 'No projectors to manage.' : 'All projectors staged for removal — Save to confirm, or undo.'}
            </Typography>
          ) : visible.map((c) => (
            <Stack key={c.id} direction="row" spacing={1.25} sx={{ alignItems: 'center', p: 1, borderRadius: 1.5, '&:hover': { bgcolor: C.hover } }}>
              <Box sx={{
                width: 40, height: 40, borderRadius: '50%', overflow: 'hidden', bgcolor: C.chip, color: C.text, display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, flexShrink: 0, textTransform: 'uppercase',
              }}
              >
                {c.image ? <Box component="img" src={c.image} alt={nameOf(c)} sx={{ width: '100%', height: '100%', objectFit: 'cover' }} /> : nameOf(c).charAt(0)}
              </Box>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography sx={{ fontWeight: 600, fontSize: 14, color: C.text }} noWrap>{nameOf(c)}</Typography>
                <Typography variant="caption" sx={{ color: C.muted }} noWrap>{c.email}</Typography>
              </Box>
              <Tooltip title="Remove projector">
                <IconButton size="small" onClick={() => remove(c.id)} disabled={saving} sx={{ color: C.danger }}>
                  <DeleteOutlineRoundedIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Stack>
          ))}
        </Box>

        <Stack direction="row" spacing={1} sx={{ alignItems: 'center', justifyContent: 'flex-end', p: 2, borderTop: `1px solid ${C.border}` }}>
          {removedIds.length > 0 && (
            <Typography variant="caption" sx={{ color: C.muted, mr: 'auto' }}>{`${removedIds.length} to remove`}</Typography>
          )}
          <Button onClick={close} disabled={saving} sx={{ textTransform: 'none', fontWeight: 600, color: C.muted, '&:hover': { color: C.text, bgcolor: C.hover } }}>
            Cancel
          </Button>
          <Button
            variant="contained"
            disableElevation
            onClick={save}
            disabled={saving}
            sx={{ textTransform: 'none', fontWeight: 700, borderRadius: 1.5, bgcolor: '#fff', color: '#15171c', '&:hover': { bgcolor: '#e9eaee' }, '&.Mui-disabled': { bgcolor: 'rgba(255,255,255,0.3)', color: 'rgba(21,23,28,0.5)' } }}
          >
            {saving ? 'Saving…' : 'Save'}
          </Button>
        </Stack>
      </DialogContent>
    </Dialog>
  );
};

export default ManageProjectorModal;
